import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { fileService } from '../../services/fileService';
import { tryParseMediaProcessingResult } from '../../types/multimodal';
import { MediaProcessingStatus } from './MediaProcessingStatus';

export interface AudioMessageProps {
  fileId: string;
  processedContentJson?: string | null;
}

/**
 * Renders an audio attachment with transcription (if available).
 */
export const AudioMessage = ({ fileId, processedContentJson }: AudioMessageProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const result = useMemo(() => tryParseMediaProcessingResult(processedContentJson), [processedContentJson]);

  useEffect(() => {
    let active = true;
    let urlToRevoke: string | null = null;

    (async () => {
      try {
        const resp = await fileService.downloadFileBlob(fileId);
        const blob = resp.data;
        urlToRevoke = URL.createObjectURL(blob);
        if (active) setObjectUrl(urlToRevoke);
      } catch {
        if (active) setObjectUrl(null);
      }
    })();

    return () => {
      active = false;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [fileId]);

  const download = async () => {
    const resp = await fileService.downloadFileBlob(fileId);
    const blob = resp.data;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attachment-${fileId}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">Audio</Typography>
        <MediaProcessingStatus processedContentJson={processedContentJson} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} variant="outlined">
          Download
        </Button>
      </Box>

      {objectUrl && <audio controls src={objectUrl} style={{ width: '100%' }} />}

      {result?.transcription && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {result.transcription}
        </Typography>
      )}
    </Box>
  );
};

